// src/HomePage.js
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { Maximize2, X } from "lucide-react";
import { db } from "./firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { onSnapshot } from "./queryMonitor"; // timed reads (Billing → Performance)
import { getClientEmail, setClientEmail as persistClientEmail, setClientAccount as persistClientAccount, findAccountByEmail } from "./clientIdentity";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Card, CardContent } from "./components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "./components/ui/dialog";
import { requestWebPushPermission } from "./webPushService";
import InstallAppCard from "./InstallAppCard";
import { useRegistrationForm, useRegistrationStatus, listNames } from "./registrationForm";
import { checkLoginEmail, requestLoginCode, verifyLoginCode, getPendingLogin, cancelPendingLogin, familyLogout } from "./familyLogin";

const NOTIF_DISMISSED_KEY = "dance_booking_notif_banner_dismissed";

function HomePage() {
  const registrationForm = useRegistrationForm();
  const registrationsOpen = !!registrationForm?.open;

  const [clientEmail, setClientEmail] = useState(getClientEmail);
  const registration = useRegistrationStatus(clientEmail, registrationForm?.year);
  const [clientAccount, setClientAccount] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dance_booking_account")); } catch { return null; }
  });
  const [clientConfirmed, setClientConfirmed] = useState(() => !!localStorage.getItem("dance_booking_confirmed"));
  const [emailInput, setEmailInput] = useState("");
  // Logging in: a known email gets a 6-digit code by WhatsApp (familyLogin.js).
  const [pendingLogin, setPendingLogin] = useState(getPendingLogin);
  const [codeInput, setCodeInput] = useState("");
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");

  const supportsWebPush = !Capacitor.isNativePlatform() && typeof Notification !== "undefined";
  const [notifPermission, setNotifPermission] = useState(supportsWebPush ? Notification.permission : "unsupported");
  const [notifDismissed, setNotifDismissed] = useState(() => {
    try { return !!localStorage.getItem(NOTIF_DISMISSED_KEY); } catch { return false; }
  });
  const [enlargedImage, setEnlargedImage] = useState(null); // highlight whose image is shown full screen

  // Keep the banner in sync if notifications get switched on or blocked
  // elsewhere (Settings page, browser site settings) while Home is open.
  useEffect(() => {
    if (!supportsWebPush) return;
    let status;
    const sync = () => setNotifPermission(Notification.permission);
    navigator.permissions?.query({ name: "notifications" }).then((st) => {
      status = st;
      status.addEventListener("change", sync);
    }).catch(() => {});
    window.addEventListener("focus", sync);
    return () => {
      status?.removeEventListener("change", sync);
      window.removeEventListener("focus", sync);
    };
  }, [supportsWebPush]);

  const [highlights, setHighlights] = useState([]);
  useEffect(() => {
    const u = onSnapshot(collection(db, "highlights"), (snap) => {
      setHighlights(
        snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((h) => h.active !== false)
          .sort((a, b) => {
            const orderDiff = (a.order ?? 0) - (b.order ?? 0);
            if (orderDiff !== 0) return orderDiff;
            return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
          })
      );
    }, (err) => console.error("HomePage highlights error:", err));
    return () => u();
  }, []);

  // An email remembered without an account (e.g. a family that registered)
  // may have become one since. Never link it silently — they log in with a
  // code like everyone else, so forget it and show the login box.
  useEffect(() => {
    if (!clientEmail || clientAccount) return;
    findAccountByEmail(clientEmail).then((acc) => {
      if (!acc) return;
      familyLogout();
      setClientEmail("");
      setClientConfirmed(false);
    }).catch(() => {});
  }, [clientEmail, clientAccount]);

  // A known email asks for the family's login code; nothing about the family
  // is shown or remembered until the code is entered. An unknown email is
  // remembered as before (no account), so they can still book and register.
  const handleEmailSubmit = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email) return;
    setLoginBusy(true);
    setLoginError("");
    try {
      const result = await checkLoginEmail(email);
      try {
        await addDoc(collection(db, "emailVisits"), {
          email, accountId: null, accountName: null, matched: result.found, timestamp: serverTimestamp(),
        });
      } catch (err) {
        console.error("Failed to log email visit:", err);
      }
      if (result.found) {
        setPendingLogin(result);
      } else {
        persistClientEmail(email);
        persistClientAccount(null);
        setClientEmail(email);
        setClientAccount(null);
      }
      setEmailInput("");
    } catch (err) {
      setLoginError(err.message);
    }
    setLoginBusy(false);
  };

  const handleCodeSubmit = async () => {
    if (!pendingLogin) return;
    setLoginBusy(true);
    setLoginError("");
    try {
      const account = await verifyLoginCode(pendingLogin.email, codeInput);
      setClientEmail(pendingLogin.email);
      setClientAccount(account);
      setClientConfirmed(true);
      setPendingLogin(null);
      setCodeInput("");
    } catch (err) {
      setLoginError(err.message);
    }
    setLoginBusy(false);
  };

  const askForCode = async () => {
    if (!pendingLogin) return;
    setLoginBusy(true);
    setLoginError("");
    try { setPendingLogin(await requestLoginCode(pendingLogin.email)); }
    catch (err) { setLoginError(err.message); }
    setLoginBusy(false);
  };

  const cancelLogin = () => {
    cancelPendingLogin();
    setPendingLogin(null);
    setCodeInput("");
    setLoginError("");
  };

  const confirmIdentity = () => {
    setClientConfirmed(true);
    localStorage.setItem("dance_booking_confirmed", "1");
  };

  const clearIdentity = () => {
    familyLogout(); // also signs out a code login on this device
    setPendingLogin(null);
    persistClientEmail("");
    persistClientAccount(null);
    localStorage.removeItem("dance_booking_confirmed");
    setClientEmail("");
    setClientAccount(null);
    setClientConfirmed(false);
  };

  // Only nudge while the browser hasn't been asked yet. Once notifications are
  // on (or blocked, which Settings explains) the banner has nothing to offer.
  const showNotifBanner = supportsWebPush && !!clientEmail && notifPermission === "default" && !notifDismissed;

  const enableNotifications = async () => {
    const result = await requestWebPushPermission();
    setNotifPermission(result);
  };

  const dismissNotifBanner = () => {
    setNotifDismissed(true);
    try { localStorage.setItem(NOTIF_DISMISSED_KEY, "1"); } catch {}
  };

  const greetingName = clientEmail && clientConfirmed && clientAccount ? clientAccount.parentName : null;

  return (
    <div className="max-w-[680px] mx-auto">
      {/* Greeting. The logo and Teacher login live in the menu/sidebar. */}
      <div className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
        <h2 className="m-0 text-2xl font-extrabold text-foreground">
          {greetingName ? `Hi, ${greetingName}` : "Welcome to Authentrix"}
        </h2>
        {greetingName && (
          <button type="button" onClick={clearIdentity} className="border-0 bg-transparent p-0 text-sm text-muted-foreground underline underline-offset-2 cursor-pointer hover:text-foreground">
            Not you?
          </button>
        )}
      </div>

      {/* How to install the app — matters most on iPhones (Safari logs out after a week) */}
      <InstallAppCard />

      {/* Waiting for the WhatsApp login code */}
      {!clientEmail && pendingLogin && (
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="text-sm font-semibold text-foreground mb-1">Enter your login code</div>
            <div className="text-sm text-muted-foreground mb-2">
              {pendingLogin.requested
                ? (pendingLogin.hasPhone
                  ? <>Thanks — the studio will WhatsApp your 6-digit code to the cellphone number on your account{pendingLogin.phoneHint ? <> (ending in <strong>{pendingLogin.phoneHint}</strong>)</> : null}. A teacher sends it, so it may take a little while — you can close the app and come back.</>
                  : <>We don't have a cellphone number on your account. Please WhatsApp Somari or Kaylee to get your login code.</>)
                : <>Enter your family's 6-digit AuthenTrix login code — the studio sent it to you by WhatsApp. It stays the same, so you can use it on any phone.</>}
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="6-digit code"
                maxLength={6}
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ""))}
                onKeyDown={(e) => e.key === "Enter" && codeInput.length === 6 && handleCodeSubmit()}
                className="flex-1 min-w-[140px] tracking-[0.3em]"
              />
              <Button onClick={handleCodeSubmit} disabled={loginBusy || codeInput.length !== 6}>{loginBusy ? "Checking…" : "Log in"}</Button>
            </div>
            {loginError && <div className="mt-2 text-sm font-semibold text-[var(--color-danger)]">{loginError}</div>}
            {!pendingLogin.requested && (
              <button type="button" onClick={askForCode} disabled={loginBusy} className="mt-2 border-0 bg-transparent p-0 text-xs text-accent underline cursor-pointer">
                Forgot your code? Ask the studio to send it again
              </button>
            )}
            <div className="mt-2 text-xs text-muted-foreground">
              Logging in as {pendingLogin.email} ·{" "}
              <button type="button" onClick={cancelLogin} className="border-0 bg-transparent p-0 text-xs text-accent underline cursor-pointer">Use a different email</button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email personalization */}
      {!clientEmail && !pendingLogin && (
        <Card className="mb-4">
          <CardContent className="p-3">
            <div className="text-sm text-muted-foreground mb-2">Enter the email your statements are sent to, to log in.</div>
            <div className="flex gap-2 items-center flex-wrap">
              <Input
                type="email"
                placeholder="your@email.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleEmailSubmit()}
                className="flex-1 min-w-[180px]"
                autoComplete="email"
              />
              <Button onClick={handleEmailSubmit} disabled={loginBusy}>{loginBusy ? "…" : "Go"}</Button>
            </div>
            {loginError && <div className="mt-2 text-sm font-semibold text-[var(--color-danger)]">{loginError}</div>}
            {registrationsOpen && (
              <div className="mt-3 text-sm text-muted-foreground">
                New to AuthenTrix? <Link to="/register" className="font-semibold text-accent">Register your dancer</Link>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Client identity banner — "Is this you?" confirmation */}
      {clientEmail && clientAccount && !clientConfirmed && (
        <Card className="mb-4 bg-[color-mix(in_oklch,var(--color-success)_15%,transparent)] border-[color-mix(in_oklch,var(--color-success)_35%,transparent)]">
          <CardContent className="p-3 text-sm">
            <div className="mb-2 text-muted-foreground">Is this you?</div>
            <div className="font-semibold text-base mb-2.5 text-foreground">{clientAccount.parentName} {clientAccount.parentSurname}</div>
            <div className="flex gap-2">
              <Button size="sm" onClick={confirmIdentity}>Yes, that's me</Button>
              <Button variant="outline" size="sm" onClick={clearIdentity}>Not me</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signed up, account not set up yet (a new family waiting for approval) */}
      {clientEmail && !clientAccount && registration && (
        <Card className="mb-4 bg-[color-mix(in_oklch,var(--color-success)_15%,transparent)] border-[color-mix(in_oklch,var(--color-success)_35%,transparent)]">
          <CardContent className="p-3 flex justify-between items-center gap-3 text-sm">
            <span className="text-foreground">
              Your registration for <strong>{listNames(registration.dancers)}</strong> has been received. We'll set up your account and be in touch soon.
            </span>
            <Button variant="outline" size="sm" onClick={clearIdentity}>Change</Button>
          </CardContent>
        </Card>
      )}

      {/* Email not on file */}
      {clientEmail && !clientAccount && registration === null && (
        <Card className="mb-4">
          <CardContent className="p-3 flex justify-between items-center gap-3 text-sm">
            <span className="text-muted-foreground">We don't have <strong>{clientEmail}</strong> on file yet. Is this the email you receive statements on? If not, try that one — otherwise please contact Authentrix to link your email to your account.{registrationsOpen && <> Not registered yet? <Link to="/register" className="font-semibold text-accent">Register here</Link>.</>}</span>
            <Button variant="outline" size="sm" onClick={clearIdentity}>Change</Button>
          </CardContent>
        </Card>
      )}

      {/* Notification reminder */}
      {showNotifBanner && (
        <Card className="mb-4 bg-[var(--color-draft-bg)] border-[var(--color-draft-border)]">
          <CardContent className="p-3 flex items-center gap-3 text-sm text-foreground">
            <span className="flex-1">🔔 Don't miss a class update — turn on notifications.</span>
            <Button size="sm" onClick={enableNotifications}>Turn on</Button>
            <button type="button" onClick={dismissNotifBanner} aria-label="Dismiss" className="border-0 bg-transparent p-1 text-muted-foreground cursor-pointer hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </CardContent>
        </Card>
      )}

      {/* Highlights — teacher-curated promo cards (new products, upcoming competitions, etc.) */}
      {highlights.length > 0 && (
        <div className="flex flex-col gap-3">
          {highlights.map((h, i) => {
            const theme = HIGHLIGHT_THEMES[i % HIGHLIGHT_THEMES.length];
            return (
              <Card
                key={h.id}
                className="overflow-hidden border-2"
                style={{ background: theme.bg, borderColor: theme.border }}
              >
                <CardContent className="p-0 flex items-stretch">
                  <div className="w-1.5 flex-shrink-0" style={{ background: theme.accent }} />
                  {h.imageUrl ? (
                    // Small thumbnail; tap to see the whole image full screen.
                    <button
                      type="button"
                      onClick={() => setEnlargedImage(h)}
                      className="relative block self-center ml-3 my-3 w-24 h-24 flex-shrink-0 overflow-hidden rounded-lg border-0 p-0 bg-transparent cursor-zoom-in shadow-sm"
                      aria-label={`View ${h.title || "image"} full screen`}
                    >
                      <img src={h.imageUrl} alt="" className="block w-24 h-24 object-cover" />
                      <span className="absolute bottom-1 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white">
                        <Maximize2 className="h-3 w-3" aria-hidden="true" />
                      </span>
                    </button>
                  ) : (
                    <div className="w-16 flex items-center justify-center text-3xl flex-shrink-0">{theme.emoji}</div>
                  )}
                  <div className="p-3 flex-1 min-w-0 flex flex-col justify-center gap-1">
                    <div className="text-base font-extrabold text-foreground">{h.title}</div>
                    {h.body && <div className="text-sm text-muted-foreground">{h.body}</div>}
                    {h.linkUrl && (
                      <a href={h.linkUrl} className="mt-1.5">
                        <Button size="sm" style={{ background: theme.accent, color: "var(--color-accent-text)", borderColor: theme.accent }}>
                          {h.linkLabel || "Learn more"}
                        </Button>
                      </a>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!enlargedImage} onOpenChange={(open) => !open && setEnlargedImage(null)}>
        <DialogContent className="max-w-[95vw] w-auto p-2 bg-black border-0 [&>button]:text-white [&>button]:opacity-90">
          <DialogTitle className="sr-only">{enlargedImage?.title || "Image"}</DialogTitle>
          {enlargedImage && (
            <img src={enlargedImage.imageUrl} alt={enlargedImage.title || ""} className="block max-w-full max-h-[85vh] mx-auto object-contain" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// A small rotating palette so consecutive highlights read as distinct without
// needing the teacher to pick colors — built from the app's existing
// theme-aware tokens so it still looks right in dark mode.
const HIGHLIGHT_THEMES = [
  { bg: "color-mix(in oklch, var(--color-accent) 12%, var(--color-surface))", border: "color-mix(in oklch, var(--color-accent) 45%, var(--color-border))", accent: "var(--color-accent)", emoji: "✨" },
  { bg: "color-mix(in oklch, var(--color-success) 14%, var(--color-surface))", border: "color-mix(in oklch, var(--color-success) 45%, var(--color-border))", accent: "var(--color-success)", emoji: "🎉" },
  { bg: "var(--color-draft-bg)", border: "var(--color-draft-border)", accent: "var(--color-draft-border)", emoji: "🏆" },
  { bg: "color-mix(in oklch, var(--color-danger) 12%, var(--color-surface))", border: "color-mix(in oklch, var(--color-danger) 40%, var(--color-border))", accent: "var(--color-danger)", emoji: "🔥" },
];

export default HomePage;
